import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import { Button, Card, CardContent, CircularProgress, Grid, IconButton, Paper, Typography } from '@material-ui/core'
import { Page, Header } from 'components'
import { useCestaOperacaoLote } from 'hooks/useCestaOperacaoLote'
import { useVestigioService } from 'hooks/useVestigioService'
import MaterialTable from 'material-table'
import moment from 'moment'
import localization from 'moment/locale/pt-br'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import FirstPageIcon from '@material-ui/icons/FirstPage'
import LastPageIcon from '@material-ui/icons/LastPage'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import useRouter from 'utils/useRouter'
import useStyles from './styles'
import { useParams } from 'react-router'
import { getStatusCadeiaCustodiaVestigio } from 'helpers/labels'
import { SelectVestigio } from 'components/SelectVestigio'

export const Lote = (props) => {
  moment.locale('pt-br', localization)
  const { route } = props
  const { idLote } = useParams()
  const classes = useStyles()
  const router = useRouter()
  const vestigioService = useVestigioService()
  const { addVestigio, verificarItemEmCesta, reloadListeners, setReloadListeners } = useCestaOperacaoLote()
  const [processandoLote, setProcessandoLote] = useState(false)
  const [lote, setLote] = useState(null)
  const [vestigiosParaAdicionar, setVestigiosParaAdicionar] = useState([])
  const [processandoAdicionarVestigioEmLote, setProcessandoAdicionarVestigioEmLote] = useState(false)

  const fetchLote = useCallback(async () => {
    setProcessandoLote(true)
    const response = await vestigioService.listarLote(idLote)
    setLote(response)
    setProcessandoLote(false)
  }, [idLote])

  useEffect(() => {
    fetchLote()
    if (reloadListeners) {
      setReloadListeners(false)
    }
  }, [fetchLote, reloadListeners])

  const adicionarVestigiosLote = useCallback(async () => {
    setProcessandoLote(true)
    for (const vestigio of vestigiosParaAdicionar) {
      await vestigioService.adicionarVestigioEmLote(vestigio.id, idLote)
    }
    setVestigiosParaAdicionar([])
    fetchLote()
  }, [vestigiosParaAdicionar, idLote])

  const getLinkRetorno = () => {
    switch (route.origem) {
      case 'BancoDeVestigios':
        return '/vestigios'
      case 'MeusVestigios':
        return '/vestigios/meusvestigios'
      case 'VestigiosSetor':
        return '/vestigios/vestigiosdosetor'
      default:
        return null
    }
  }

  const tituloRetorno = () => {
    switch (route.origem) {
      case 'BancoDeVestigios':
        return 'Banco de Vestígios'
      case 'MeusVestigios':
        return 'Meus Vestígios'
      case 'VestigiosSetor':
        return 'Vestígios do Setor'
      default:
        return null
    }
  }

  const removerVestigioLote = useCallback(async (rowData) => {
    setProcessandoLote(true)
    await vestigioService.removerVestigioDeLote(rowData.id)
    await fetchLote()
    setProcessandoLote(false)
  }, [])

  const adicionarTodosCesta = useCallback(() => {
    lote?.vestigios.forEach((v) => {
      if (!verificarItemEmCesta(v.id)) {
        addVestigio(v)
      }
    })
  }, [lote])

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} linkRetorno={getLinkRetorno()} tituloLinkRetorno={tituloRetorno()} />
      <Card className={classes.results}>
        <CardContent>
          {processandoLote ? (
            <div className={classes.carregando}>
              <CircularProgress disableShrink />
            </div>
          ) : (
            <>
              <Grid container xs={12} sm={12} md={12} justifyContent="space-between" className={classes.menuSuperior}>
                <Grid item xs={12} sm={10} md={8} className={classes.menuPesquisa}>
                  <div className={classes.inputPesquisa}>
                    <SelectVestigio size="small" searchIcon vestigiosExternos={vestigiosParaAdicionar} setVestigiosExternos={setVestigiosParaAdicionar} setProcessandoImportarVestigio={processandoAdicionarVestigioEmLote} multiplo />
                  </div>
                  <Button disabled={!vestigiosParaAdicionar?.length} variant="text" color="primary" onClick={adicionarVestigiosLote}>
                    Adicionar ao lote
                  </Button>
                </Grid>

                <Button style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }} onClick={adicionarTodosCesta}>
                  <img src="/images/cesta-add-black.png" alt="Adicionar à cesta" className="img-icon-invert-dark" style={{ width: 22, height: 22, marginRight: '10px' }} />
                  <Typography>Adicionar todos à cesta</Typography>
                </Button>
              </Grid>

              <Grid container xs={12} sm={12} md={12}>
                <Grid item xs={12} sm={12} md={12}>
                  <MaterialTable
                    components={{ Container: (props) => <Paper {...props} elevation={0} /> }}
                    localization={{
                      body: {
                        emptyDataSourceMessage: processandoLote ? <CircularProgress disableShrink /> : 'Nenhum vestígio',
                        filterRow: {
                          filterTooltip: 'Filtro',
                        },
                      },
                      header: {
                        actions: 'Ações',
                      },
                    }}
                    title=""
                    columns={[
                      {
                        title: 'Identificador',
                        field: 'identificador',
                      },
                      {
                        title: 'Descrição',
                        field: 'descricao',
                      },
                      {
                        title: 'Setor atual',
                        render: (rowData) => (rowData.setAtual ? `${rowData.setAtual.nome.toUpperCase()} / ${rowData.setAtual.divisao.sigla.toUpperCase()} / ${rowData.setAtual.divisao.regional.sigla.toUpperCase()}` : '-'),
                      },
                      {
                        title: 'Status',
                        render: (rowData) => getStatusCadeiaCustodiaVestigio(rowData),
                      },
                      {
                        title: 'Data do cadastro',
                        render: (rowData) => moment(rowData.createdAt).format('LLLL'),
                      },
                    ]}
                    onRowClick={(event, rowData, togglePanel) => {
                      router.history.push(`/casos/caso/vestigio/detalhar/${rowData.id}`)
                    }}
                    data={lote?.vestigios}
                    actions={[
                      (rowData) => ({
                        icon: () => <img src="/images/cesta-add-black.png" alt="Adicionar à cesta" className="img-icon-invert-dark" style={{ width: 26, height: 26 }} />,
                        tooltip: 'Adicionar à cesta',
                        onClick: (event, rowData) => addVestigio(rowData),
                        hidden: verificarItemEmCesta(rowData.id),
                      }),
                      {
                        icon: () => <RemoveCircleOutlineIcon fontSize="small" />,
                        tooltip: 'Remover do lote',
                        onClick: (event, rowData) => removerVestigioLote(rowData),
                      },
                    ]}
                    options={{
                      actionsColumnIndex: -1,
                      search: false,
                      pageSize: 20,

                      headerStyle: {
                        backgroundColor: '#f4f6f8',
                      },
                      toolbar: false,
                    }}
                    icons={{
                      SortArrow: forwardRef((propsSortArrow, ref) => <ArrowDownwardIcon {...propsSortArrow} ref={ref} />),
                      Search: forwardRef((propsSearch, ref) => <SearchIcon {...propsSearch} ref={ref} />),
                      ResetSearch: forwardRef((propsResetSearch, ref) => <ClearIcon {...propsResetSearch} ref={ref} />),
                      NextPage: forwardRef((propsNextPage, ref) => <NavigateNextIcon {...propsNextPage} ref={ref} />),
                      DetailPanel: forwardRef((propsNextPage, ref) => <NavigateNextIcon {...propsNextPage} ref={ref} />),
                      PreviousPage: forwardRef((propsPreviousPage, ref) => <NavigateBeforeIcon {...propsPreviousPage} ref={ref} />),
                      FirstPage: forwardRef((propsFirstPage, ref) => <FirstPageIcon {...propsFirstPage} ref={ref} />),
                      LastPage: forwardRef((propsLastPage, ref) => <LastPageIcon {...propsLastPage} ref={ref} />),
                    }}
                    style={{
                      padding: '16px',
                      fontSize: '13px',
                      textAlign: 'left',
                      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                      fontWeight: '400',
                      lineHeight: '21px',

                      letterSpacing: '-0.05px',
                      verticalAlign: 'inherit',
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}
